/* global Word, Office */

import { applyDraftStyle } from "@/lib/styleService";
import {
  applyScriptMarkerFormattingToRange,
  createCaptionsForSection,
  createCrossReferences,
  createImmediateCaption,
  getNextCaptionNumber,
  insertSeqField,
  resetCaptionCounters,
  TableInfo,
  updateAllFields,
} from "./wordCaptions";
import { formatSection } from "./wordFormatting";
import Logger from "@/utility/logger";
import { createMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";
import type { GroundingInsertMetadata } from "@/types/grounding";
import { buildGroundingReferenceBlocks, GROUNDING_REFERENCE_MARKER } from "@/utility/groundingMarkers";
type CaptionType = "Table" | "Figure";

export type CaptionFontStyle = "normal" | "italics" | "bold" | "bold_italics";

export type SeqType =
  | "arabic"
  | "alphabetic_lower_caps"
  | "alphabetic_upper_caps"
  | "roman_lower_caps"
  | "roman_upper_caps";

interface ParsedTableCaptionLine {
  id: string;
  referenceText: string;
  description: string;
}

interface TableInstruction {
  showCaption: boolean;
  captionPosition: "above" | "below";
  captionText?: string;
  captionFontStyle: CaptionFontStyle;
  footnotes?: string[];
  showHeaderRow: boolean;
  headerRowCount: number;
  repeatHeaderRow: boolean;
  id?: string;
  seqType?: SeqType;
}

/**
 * Matches a table identifier in one of two formats:
 *
 * 1. Bracketed form:   [A1], [B02], [XYZ], [Y]
 *    Pattern: \[[A-Z0-9]+\]
 *
 * 2. Plain/structured form: 1, A1, 1.2, 1.2-1, #A3, B1.2#3
 *    Pattern: #?[A-Z0-9]+(?:[.#-][A-Z0-9]+)*
 *    - Optional leading `#`
 *    - One or more alphanumeric segments
 *    - Segments joined by `.`, `#`, or `-`
 *
 */
const TABLE_REFERENCE_ID_PATTERN = String.raw`(?:\[[A-Z0-9]+\]|#?[A-Z0-9]+(?:[.#-][A-Z0-9]+)*)`;

/**
 * Matches a full table caption line in loosely-formatted markdown or plain text.
 * Case-insensitive. Captures three groups:
 *
 *   Group 1 - Table ID  (e.g. "1.2-1", "A1", "[X]")
 *   Group 2 - Description text  (e.g. "Summary of results")
 *   Group 3 - Trailing markdown noise (discarded)
 *
 * Anatomy:
 *
 *   ^[#*~\s_]*          - strip any leading markdown symbols (##, **, ~~, _)
 *   Table               - literal word "Table"
 *   \s*[-.:]?\s*        - optional separator between "Table" and the ID (e.g. "Table - 1", "Table: 1")
 *   (ID_PATTERN)        - [Group 1] the table ID
 *   (?:\s*[:.]\s*|\s+)? - optional separator between ID and description (":", ".", or whitespace)
 *   (.*?)               - [Group 2] description, lazy to avoid swallowing trailing noise
 *   (\s*[#*~_]*)$       - [Group 3] trailing markdown symbols, ignored
 *
 * Examples matched:
 *   "## Table 1.2-1: Summary"   ->  id="1.2-1",  desc="Summary"
 *   "Table 3. Final Outputs"    ->  id="3",      desc="Final Outputs"
 *   "* Table A1 Overview *"     ->  id="A1",     desc="Overview"
 *   "Table [X] Description"     ->  id="[X]",    desc="Description"
 */
const TABLE_CAPTION_LINE_REGEX = new RegExp(
  String.raw`^[#*~\s_]*Table\s*[-.:]?\s*(${TABLE_REFERENCE_ID_PATTERN})(?:\s*[:.]\s*|\s+)?(.*?)(\s*[#*~_]*)$`,
  "i"
);

const stripMatchingCaptionWrapper = (
  value: string,
  wrapperStart: string,
  wrapperEnd: string = wrapperStart
): string | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith(wrapperStart) || !trimmedValue.endsWith(wrapperEnd)) {
    return null;
  }

  const unwrappedValue = trimmedValue.slice(wrapperStart.length, trimmedValue.length - wrapperEnd.length).trim();

  return unwrappedValue || null;
};

const normalizeCaptionText = (value: string): string => {
  let normalizedValue = value.trim();

  if (!normalizedValue) {
    return normalizedValue;
  }

  let previousValue = "";

  while (normalizedValue && normalizedValue !== previousValue) {
    previousValue = normalizedValue;

    const strippedValue =
      stripMatchingCaptionWrapper(normalizedValue, "**") ??
      stripMatchingCaptionWrapper(normalizedValue, "__") ??
      stripMatchingCaptionWrapper(normalizedValue, "*") ??
      stripMatchingCaptionWrapper(normalizedValue, "_") ??
      stripMatchingCaptionWrapper(normalizedValue, "~~");

    if (!strippedValue) {
      break;
    }

    normalizedValue = strippedValue;
  }

  return normalizedValue.trim();
};

export const parseTableCaptionLine = (line: string): ParsedTableCaptionLine | null => {
  const match = normalizeCaptionText(line).match(TABLE_CAPTION_LINE_REGEX);

  if (!match) {
    return null;
  }

  /**
   * Strip any residual separator character that bled into the description from Group 2.
   *
   * Examples:
   *   "- Summary of results"  ->  "Summary of results"
   *   ". Final Outputs"       ->  "Final Outputs"
   *   ": Overview"            ->  "Overview"
   *   "Overview"              ->  "Overview"   (no match, no change)
   */
  const id = match[1].trim();
  const description = (match[2] || "").trim().replace(/^[:.-]\s*/, "");

  return {
    id,
    referenceText: `Table ${id}`,
    description,
  };
};

const shouldUseStaticTableCaptionLabel = (id: string): boolean => {
  return !/^\d+$/.test(id.trim());
};

const resolveTableCaptionSource = (
  captionSource: string,
  allowPlainDescription: boolean
): {
  captionDescription: string;
  staticReferenceText: string | null;
  staticCaptionLabel: string | null;
} | null => {
  const normalizedCaptionSource = normalizeCaptionText(captionSource);

  if (!normalizedCaptionSource) {
    return null;
  }

  const tableMatch = parseTableCaptionLine(normalizedCaptionSource);

  if (tableMatch) {
    return {
      captionDescription: tableMatch.description || tableMatch.referenceText,
      staticReferenceText: tableMatch.referenceText,
      staticCaptionLabel: shouldUseStaticTableCaptionLabel(tableMatch.id) ? tableMatch.referenceText : null,
    };
  }

  if (!allowPlainDescription) {
    return null;
  }

  return {
    captionDescription: normalizedCaptionSource,
    staticReferenceText: null,
    staticCaptionLabel: null,
  };
};

const DEFAULT_TABLE_INSTRUCTION: TableInstruction = {
  showCaption: false,
  captionPosition: "above",
  captionFontStyle: "bold_italics",
  showHeaderRow: false,
  headerRowCount: 1,
  repeatHeaderRow: false,
};

const getFirstDefinedValue = (source: any, keys: string[]) => {
  for (const key of keys) {
    if (source && source[key] !== undefined) {
      return source[key];
    }
  }

  return undefined;
};

const parseBooleanValue = (value: any, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") return true;
    if (normalizedValue === "false") return false;
  }

  return fallback;
};

const parseHeaderRowCount = (value: any): number => {
  const parsedValue = parseInt(String(value === undefined ? DEFAULT_TABLE_INSTRUCTION.headerRowCount : value), 10);

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : DEFAULT_TABLE_INSTRUCTION.headerRowCount;
};

const parseFootnotes = (value: any): string[] | undefined => {
  const normalizeFootnote = (entry: unknown): string | null => {
    if (typeof entry !== "string") {
      return null;
    }

    const trimmedEntry = entry.trim();
    return trimmedEntry ? trimmedEntry : null;
  };

  if (Array.isArray(value)) {
    const normalizedFootnotes = value.map(normalizeFootnote).filter((entry): entry is string => !!entry);
    return normalizedFootnotes.length > 0 ? normalizedFootnotes : undefined;
  }

  const singleFootnote = normalizeFootnote(value);
  return singleFootnote ? [singleFootnote] : undefined;
};

const parseCaptionFontStyle = (value: any): CaptionFontStyle => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  if (normalizedValue === "bold") return "bold";
  if (normalizedValue === "italics") return "italics";
  if (normalizedValue === "bold_italics") return "bold_italics";
  if (normalizedValue === "normal") return "normal";

  return "bold";
};

const getInstructionCaptionText = (payload: any): string | undefined => {
  const explicitCaptionText = getFirstDefinedValue(payload, ["captionText"]);

  if (typeof explicitCaptionText === "string" && explicitCaptionText.trim()) {
    return normalizeCaptionText(explicitCaptionText);
  }

  const captionValue = payload?.caption;

  if (typeof captionValue === "string") {
    const normalizedCaptionValue = normalizeCaptionText(captionValue);

    if (normalizedCaptionValue && !/^(true|false)$/i.test(normalizedCaptionValue)) {
      return normalizedCaptionValue;
    }
  }

  return undefined;
};

const VALID_SEQ_TYPES = new Set<SeqType>([
  "arabic",
  "alphabetic_lower_caps",
  "alphabetic_upper_caps",
  "roman_lower_caps",
  "roman_upper_caps",
]);

const parseSeqType = (value: any): SeqType | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const normalized = value.trim().toLowerCase() as SeqType;
  return VALID_SEQ_TYPES.has(normalized) ? normalized : undefined;
};

const parseId = (value: any): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const str = String(value).trim();
  return str.length > 0 ? str : undefined;
};

const parseTableInstruction = (payload: any): TableInstruction => {
  const captionPosition = String(
    getFirstDefinedValue(payload, ["captionPosition"]) || DEFAULT_TABLE_INSTRUCTION.captionPosition
  )
    .trim()
    .toLowerCase();
  const captionText = getInstructionCaptionText(payload);
  const captionVisibilityValue = getFirstDefinedValue(payload, ["showCaption"]);
  const id = parseId(getFirstDefinedValue(payload, ["id"]));
  let seqType = parseSeqType(getFirstDefinedValue(payload, ["seqType"]));

  if (!seqType && id) {
    if (/^[IVXLCDM]+$/.test(id.trim())) {
      seqType = "roman_upper_caps";
    } else if (/^[ivxlcdm]+$/.test(id.trim())) {
      seqType = "roman_lower_caps";
    }
  }

  return {
    showCaption: parseBooleanValue(
      captionVisibilityValue,
      captionText || (id && seqType) ? true : DEFAULT_TABLE_INSTRUCTION.showCaption
    ),
    captionPosition: captionPosition === "below" ? "below" : "above",
    captionText,
    captionFontStyle: parseCaptionFontStyle(getFirstDefinedValue(payload, ["captionFontStyle"])),
    footnotes: parseFootnotes(getFirstDefinedValue(payload, ["footnotes"])),
    showHeaderRow: parseBooleanValue(
      getFirstDefinedValue(payload, ["showHeaderRow"]),
      DEFAULT_TABLE_INSTRUCTION.showHeaderRow
    ),
    headerRowCount: parseHeaderRowCount(getFirstDefinedValue(payload, ["headerRowCount"])),
    repeatHeaderRow: parseBooleanValue(
      getFirstDefinedValue(payload, ["repeatHeaderRow"]),
      DEFAULT_TABLE_INSTRUCTION.repeatHeaderRow
    ),
    id,
    seqType,
  };
};

const JENLY_TABLE_TOKEN = "JENLY_TABLE";

const findJsonObjectAfterToken = (
  source: string,
  tokenEndIndex: number
): { jsonText: string; endIndex: number } | null => {
  const jsonStartMatch = source.slice(tokenEndIndex).match(/^\s*\{/);

  if (!jsonStartMatch) {
    return null;
  }

  const jsonStartIndex = tokenEndIndex + jsonStartMatch[0].length - 1;
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = jsonStartIndex; index < source.length; index++) {
    const char = source[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === "{") {
      depth++;
      continue;
    }

    if (char === "}") {
      depth--;

      if (depth === 0) {
        return {
          jsonText: source.slice(jsonStartIndex, index + 1),
          endIndex: index + 1,
        };
      }
    }
  }

  return null;
};

const isRelaxedIdentifierStart = (char: string | undefined): boolean => !!char && /[A-Za-z_$]/.test(char);

const normalizeTableInstructionPayloadKey = (key: string): string => {
  return key.trim().replace(/^[^A-Za-z0-9_$]+/, "").replace(/[^A-Za-z0-9_$]+$/, "");
};

const normalizeTableInstructionPayloadKeys = (payload: Record<string, unknown>): Record<string, unknown> => {
  return Object.entries(payload).reduce<Record<string, unknown>>((normalizedPayload, [key, value]) => {
    const normalizedKey = normalizeTableInstructionPayloadKey(key);
    normalizedPayload[normalizedKey || key] = value;
    return normalizedPayload;
  }, {});
};

const parseRelaxedString = (source: string, startIndex: number): { value: string; nextIndex: number } => {
  const quote = source[startIndex];

  if (quote !== '"' && quote !== "'") {
    throw new Error(`Expected quoted string at position ${startIndex}.`);
  }

  let value = "";
  let index = startIndex + 1;

  while (index < source.length) {
    const char = source[index];

    if (char === "\\") {
      const escapedChar = source[index + 1];

      if (escapedChar === undefined) {
        throw new Error("Unterminated escape sequence in relaxed JENLY_TABLE payload.");
      }

      if (escapedChar === "n") {
        value += "\n";
      } else if (escapedChar === "r") {
        value += "\r";
      } else if (escapedChar === "t") {
        value += "\t";
      } else if (escapedChar === "\\" || escapedChar === '"' || escapedChar === "'") {
        value += escapedChar;
      } else {
        value += escapedChar;
      }

      index += 2;
      continue;
    }

    if (char === quote) {
      return {
        value,
        nextIndex: index + 1,
      };
    }

    value += char;
    index++;
  }

  throw new Error("Unterminated string in relaxed JENLY_TABLE payload.");
};

const parseRelaxedTableInstructionPayload = (source: string): Record<string, unknown> => {
  const trimmedSource = source.trim();

  if (!trimmedSource.startsWith("{") || !trimmedSource.endsWith("}")) {
    throw new Error("Relaxed JENLY_TABLE payload must be enclosed in braces.");
  }

  let index = 0;

  const skipWhitespace = () => {
    while (index < trimmedSource.length && /\s/.test(trimmedSource[index])) {
      index++;
    }
  };

  const parseKey = (): string => {
    skipWhitespace();

    const char = trimmedSource[index];

    if (char === '"' || char === "'") {
      const parsedString = parseRelaxedString(trimmedSource, index);
      index = parsedString.nextIndex;
      const normalizedKey = normalizeTableInstructionPayloadKey(parsedString.value);

      if (!normalizedKey) {
        throw new Error(`Invalid relaxed JENLY_TABLE key at position ${index}.`);
      }

      return normalizedKey;
    }

    const keyStart = index;

    while (
      index < trimmedSource.length &&
      trimmedSource[index] !== ":" &&
      trimmedSource[index] !== "," &&
      trimmedSource[index] !== ";" &&
      trimmedSource[index] !== "}"
    ) {
      index++;
    }

    const normalizedKey = normalizeTableInstructionPayloadKey(trimmedSource.slice(keyStart, index));

    if (!normalizedKey || !isRelaxedIdentifierStart(normalizedKey[0])) {
      throw new Error(`Invalid relaxed JENLY_TABLE key at position ${keyStart}.`);
    }

    return normalizedKey;
  };

  const parseBareValue = (): string | number | boolean | null => {
    const valueStart = index;

    while (index < trimmedSource.length) {
      const char = trimmedSource[index];

      if (char === "," || char === ";" || char === "}" || /\s/.test(char)) {
        break;
      }

      index++;
    }

    const token = trimmedSource.slice(valueStart, index).trim();

    if (!token) {
      throw new Error(`Missing relaxed JENLY_TABLE value at position ${valueStart}.`);
    }

    if (/^(true|false)$/i.test(token)) {
      return token.toLowerCase() === "true";
    }

    if (/^null$/i.test(token)) {
      return null;
    }

    if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(token)) {
      return Number(token);
    }

    return token;
  };

  const parseValue = (): unknown => {
    skipWhitespace();

    const char = trimmedSource[index];

    if (char === '"' || char === "'") {
      const parsedString = parseRelaxedString(trimmedSource, index);
      index = parsedString.nextIndex;
      return parsedString.value;
    }

    if (char === "{") {
      const objectStart = index;
      let depth = 0;
      let inString = false;
      let stringQuote = "";
      let escaped = false;

      while (index < trimmedSource.length) {
        const currentChar = trimmedSource[index];

        if (inString) {
          if (escaped) {
            escaped = false;
          } else if (currentChar === "\\") {
            escaped = true;
          } else if (currentChar === stringQuote) {
            inString = false;
          }

          index++;
          continue;
        }

        if (currentChar === '"' || currentChar === "'") {
          inString = true;
          stringQuote = currentChar;
          index++;
          continue;
        }

        if (currentChar === "{") {
          depth++;
        } else if (currentChar === "}") {
          depth--;

          if (depth === 0) {
            index++;
            return parseRelaxedTableInstructionPayload(trimmedSource.slice(objectStart, index));
          }
        }

        index++;
      }

      throw new Error("Unterminated nested object in relaxed JENLY_TABLE payload.");
    }

    if (char === "[") {
      index++;
      const values: unknown[] = [];

      while (index < trimmedSource.length) {
        skipWhitespace();

        if (trimmedSource[index] === "]") {
          index++;
          break;
        }

        values.push(parseValue());
        skipWhitespace();

        if (trimmedSource[index] === "," || trimmedSource[index] === ";") {
          index++;
          continue;
        }

        if (trimmedSource[index] === "]") {
          index++;
          break;
        }

        throw new Error(`Unexpected token in relaxed JENLY_TABLE array at position ${index}.`);
      }

      return values;
    }

    return parseBareValue();
  };

  const result: Record<string, unknown> = {};

  skipWhitespace();
  if (trimmedSource[index] !== "{") {
    throw new Error("Relaxed JENLY_TABLE payload must start with '{'.");
  }
  index++;

  while (index < trimmedSource.length) {
    skipWhitespace();

    if (trimmedSource[index] === "}") {
      index++;
      break;
    }

    const key = parseKey();

    skipWhitespace();
    if (trimmedSource[index] !== ":") {
      throw new Error(`Expected ':' after relaxed JENLY_TABLE key "${key}".`);
    }
    index++;

    const value = parseValue();
    result[key] = value;

    skipWhitespace();
    if (trimmedSource[index] === "," || trimmedSource[index] === ";") {
      index++;
      continue;
    }

    if (trimmedSource[index] === "}") {
      index++;
      break;
    }

    if (index < trimmedSource.length) {
      throw new Error(`Unexpected token in relaxed JENLY_TABLE payload at position ${index}.`);
    }
  }

  skipWhitespace();
  if (index !== trimmedSource.length) {
    throw new Error("Unexpected trailing content in relaxed JENLY_TABLE payload.");
  }

  return result;
};

const parseTableInstructionPayload = (jsonText: string): Record<string, unknown> | null => {
  try {
    return normalizeTableInstructionPayloadKeys(JSON.parse(jsonText));
  } catch (jsonError) {
    try {
      return normalizeTableInstructionPayloadKeys(parseRelaxedTableInstructionPayload(jsonText));
    } catch (relaxedError) {
      Logger.warn(
        "wordInsertion.ts: Dropping JENLY_TABLE token because its payload could not be parsed as JSON or relaxed syntax.",
        {
          jsonError,
          relaxedError,
        }
      );
      return null;
    }
  }
};

const normalizeTextAfterTableInstructionRemoval = (content: string): string => {
  return content
    .replace(/[ \t]+\n/g, "\n")
    .trim();
};

export const extractTableInstructionsFromText = (
  content: string
): {
  content: string;
  instructions: TableInstruction[];
} => {
  const instructions: TableInstruction[] = [];
  let cleanedContent = "";
  let cursor = 0;

  while (cursor < content.length) {
    const tokenIndex = content.indexOf(JENLY_TABLE_TOKEN, cursor);

    if (tokenIndex === -1) {
      cleanedContent += content.slice(cursor);
      break;
    }

    cleanedContent += content.slice(cursor, tokenIndex);

    const tokenEndIndex = tokenIndex + JENLY_TABLE_TOKEN.length;
    const jsonObject = findJsonObjectAfterToken(content, tokenEndIndex);

    if (!jsonObject) {
      Logger.warn("wordInsertion.ts: Dropping JENLY_TABLE token because no JSON object was found.");
      cursor = tokenEndIndex;
      continue;
    }

    const parsedPayload = parseTableInstructionPayload(jsonObject.jsonText);

    if (parsedPayload) {
      instructions.push(parseTableInstruction(parsedPayload));
    }

    cursor = jsonObject.endIndex;
  }

  return {
    content: normalizeTextAfterTableInstructionRemoval(cleanedContent),
    instructions,
  };
};

const getHeaderRowsToStyle = (instruction: TableInstruction | null, rowCount: number): number => {
  if (rowCount <= 0) {
    return 0;
  }

  if (!instruction) {
    return 1;
  }

  return Math.min(instruction.headerRowCount, rowCount);
};

const getRepeatingHeaderRowCount = (instruction: TableInstruction | null, headerRowsToStyle: number): number => {
  if (!instruction) {
    return headerRowsToStyle;
  }

  return instruction.repeatHeaderRow ? headerRowsToStyle : 0;
};

const shouldCreateTableCaption = (instruction: TableInstruction | null): boolean => {
  return instruction ? instruction.showCaption : true;
};

const applyCaptionFontStyle = (captionParagraph: Word.Paragraph, captionFontStyle: CaptionFontStyle) => {
  captionParagraph.font.bold = captionFontStyle === "bold" || captionFontStyle === "bold_italics";
  captionParagraph.font.italic =
    captionFontStyle === "italics" || captionFontStyle === "bold_italics" || captionFontStyle === "bold";
};

const insertTableFootnotes = async (
  context: Word.RequestContext,
  anchorParagraph: Word.Paragraph,
  footnotes: string[] | undefined
) => {
  if (!footnotes || footnotes.length === 0) {
    return anchorParagraph;
  }

  let currentParagraph = anchorParagraph;

  for (const footnote of footnotes) {
    prepareTextInsertionParagraph(currentParagraph);
    currentParagraph.insertText(footnote, Word.InsertLocation.replace);

    const footnoteRange = currentParagraph.getRange();
    await applyDraftStyle(footnoteRange);
    await applyScriptMarkerFormattingToRange(context, footnoteRange);

    currentParagraph = currentParagraph.insertParagraph("", Word.InsertLocation.after);
  }

  prepareTextInsertionParagraph(currentParagraph);
  await context.sync();

  return currentParagraph;
};

export { insertSeqField, resetCaptionCounters, getNextCaptionNumber };

let lastWordRun: Promise<any> = Promise.resolve();

export function queuedWordRun<T>(callback: (context: Word.RequestContext) => Promise<T>): Promise<T> {
  const run = () =>
    Word.run(async (context) => {
      try {
        return await callback(context);
      } catch (err) {
        Logger.error("wordInsertion.ts: Word.run error during content insertion into document: ", {
          name: err?.name,
          message: err?.message,
          code: err?.code,
          debugInfo: err?.debugInfo,
        });
        throw err;
      }
    });

  lastWordRun = lastWordRun.then(run, run);
  return lastWordRun;
}

const prepareTextInsertionParagraph = (paragraph: Word.Paragraph, draftStyles?: any) => {
  paragraph.styleBuiltIn = Word.BuiltInStyleName.normal;
  paragraph.alignment = Word.Alignment.justified;
  if (paragraph.font) {
    paragraph.font.bold = false;
    if (draftStyles?.fontFamily) {
      paragraph.font.name = draftStyles.fontFamily;
    }
    if (draftStyles?.fontSize) {
      paragraph.font.size = draftStyles.fontSize;
    }
    if (draftStyles?.fontColor) {
      paragraph.font.color = draftStyles.fontColor;
    }
  }
};

const applyGroundingMarkerContentControls = async (
  context: Word.RequestContext,
  sectionRange: Word.Range,
  groundingMetadata?: GroundingInsertMetadata | null
) => {
  try {
    Logger.debug("wordInsertion.ts: Grounding link creation started.", {
      taskId: groundingMetadata?.taskId || null,
      hasMarkdown: Boolean(groundingMetadata?.markdown),
    });

    if (!groundingMetadata?.taskId || !groundingMetadata.markdown) {
      Logger.warn("wordInsertion.ts: Grounding link creation skipped: missing task id or markdown.");
      return;
    }

    Logger.debug("wordInsertion.ts: Building grounding reference blocks from markdown.", {
      taskId: groundingMetadata.taskId,
      markdownLength: groundingMetadata.markdown.length,
    });

    const blocks = buildGroundingReferenceBlocks(groundingMetadata.taskId, groundingMetadata.markdown);
    if (blocks.length === 0) {
      Logger.warn("wordInsertion.ts: Grounding link creation stopped: no reference blocks were found.", {
        taskId: groundingMetadata.taskId,
        markdownLength: groundingMetadata.markdown.length,
      });
      return;
    }

    Logger.debug("wordInsertion.ts: Grounding reference blocks built.", {
      taskId: groundingMetadata.taskId,
      referenceBlocks: blocks.length,
      blockIds: blocks.map((block) => block.id),
    });

    Logger.debug("wordInsertion.ts: Searching inserted Word section for grounding marker glyphs.", {
      taskId: groundingMetadata.taskId,
      marker: GROUNDING_REFERENCE_MARKER,
    });

    let matches = sectionRange.search(GROUNDING_REFERENCE_MARKER, {
      matchCase: true,
      matchWholeWord: false,
    });
    matches.load("items");
    await context.sync();

    if (matches.items.length !== blocks.length) {
      Logger.warn("wordInsertion.ts: Grounding marker count does not match reference block count.", {
        taskId: groundingMetadata.taskId,
        markerMatches: matches.items.length,
        referenceBlocks: blocks.length,
      });
    } else {
      Logger.debug("wordInsertion.ts: Grounding marker matches found.", {
        taskId: groundingMetadata.taskId,
        markerMatches: matches.items.length,
        referenceBlocks: blocks.length,
      });
    }

    const matchedBlockCount = Math.min(matches.items.length, blocks.length);
    const boundarySpacerBlocks = blocks
      .slice(0, matchedBlockCount)
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => block.requiresWordBoundarySpacer);

    if (boundarySpacerBlocks.length > 0) {
      Logger.debug("wordInsertion.ts: Applying grounding marker boundary spacers.", {
        taskId: groundingMetadata.taskId,
        spacerCount: boundarySpacerBlocks.length,
        blockIds: boundarySpacerBlocks.map(({ block }) => block.id),
      });

      for (const { index } of boundarySpacerBlocks) {
        matches.items[index].insertText(" ", Word.InsertLocation.after);
      }

      await context.sync();

      matches = sectionRange.search(GROUNDING_REFERENCE_MARKER, {
        matchCase: true,
        matchWholeWord: false,
      });
      matches.load("items");
      await context.sync();

      if (matches.items.length !== blocks.length) {
        Logger.warn("wordInsertion.ts: Grounding marker count changed after boundary spacer insertion.", {
          taskId: groundingMetadata.taskId,
          markerMatches: matches.items.length,
          referenceBlocks: blocks.length,
        });
      }
    }

    const countToApply = Math.min(matches.items.length, blocks.length);
    if (countToApply === 0) return;

    // Check parent content controls to avoid invalid nested content controls
    const parentControls = matches.items.slice(0, countToApply).map((m) => {
      const pc = m.parentContentControlOrNullObject;
      pc?.load?.("isNullObject, tag");
      return pc;
    });
    await context.sync();

    try {
      for (let index = 0; index < countToApply; index++) {
        const match = matches.items[index];
        const block = blocks[index];

        const title = block.sourceOnly ? "Sources" : `Rationale ${block.id.replace(/^\D+/, "")}`;
        Logger.debug("wordInsertion.ts: Creating grounding content control for marker.", {
          taskId: groundingMetadata.taskId,
          index,
          blockId: block.id,
          blockKind: block.kind,
          sourceIds: block.sourceIds,
          tag: block.tag,
          title,
          boundarySpacerApplied: block.requiresWordBoundarySpacer,
        });

        match.font.underline = Word.UnderlineType.none;

        const parentControl = parentControls[index];
        let control: Word.ContentControl;
        if (parentControl && !parentControl.isNullObject) {
          control = parentControl;
        } else {
          control = match.insertContentControl();
        }
        control.tag = block.tag;
        control.title = title;
        control.appearance = Word.ContentControlAppearance.boundingBox;
        control.cannotEdit = false;
        control.cannotDelete = false;
      }

      await context.sync();
      Logger.debug("wordInsertion.ts: Grounding link creation finished (batch).", {
        taskId: groundingMetadata.taskId,
        appliedCount: countToApply,
      });
    } catch (batchError) {
      Logger.warn(
        "wordInsertion.ts: Batch content control creation failed, falling back to individual per-marker creation:",
        batchError
      );

      try {
        const fallbackMatches = sectionRange.search(GROUNDING_REFERENCE_MARKER, {
          matchCase: true,
          matchWholeWord: false,
        });
        fallbackMatches.load("items");
        await context.sync();

        const fallbackCount = Math.min(fallbackMatches.items.length, blocks.length);
        const fallbackParents = fallbackMatches.items.slice(0, fallbackCount).map((m) => {
          const pc = m.parentContentControlOrNullObject;
          pc?.load?.("isNullObject, tag");
          return pc;
        });
        await context.sync();

        let successfulCount = 0;
        for (let index = 0; index < fallbackCount; index++) {
          const match = fallbackMatches.items[index];
          const block = blocks[index];
          const title = block.sourceOnly ? "Sources" : `Rationale ${block.id.replace(/^\D+/, "")}`;
          const pc = fallbackParents[index];

          try {
            match.font.underline = Word.UnderlineType.none;
            let control: Word.ContentControl;
            if (pc && !pc.isNullObject) {
              control = pc;
            } else {
              control = match.insertContentControl();
            }
            control.tag = block.tag;
            control.title = title;
            control.appearance = Word.ContentControlAppearance.boundingBox;
            control.cannotEdit = false;
            control.cannotDelete = false;
            await context.sync();
            successfulCount++;
          } catch (singleError) {
            Logger.warn(
              `wordInsertion.ts: Failed to create content control for marker at index ${index} (${block.id}):`,
              singleError
            );
          }
        }

        Logger.debug("wordInsertion.ts: Grounding link creation finished (fallback).", {
          taskId: groundingMetadata.taskId,
          successfulCount,
          totalBlocks: blocks.length,
        });
      } catch (fallbackSearchError) {
        Logger.error("wordInsertion.ts: Failed during grounding marker fallback creation:", fallbackSearchError);
      }
    }
  } catch (error) {
    Logger.error("wordInsertion.ts: Unexpected error in applyGroundingMarkerContentControls:", error);
  }
};

const DEFAULT_PAGE_WIDTH = 595;
const DEFAULT_PAGE_HEIGHT = 842;
const DEFAULT_PAGE_MARGIN = 24;

const getSectionImageFitBox = async (
  context: Word.RequestContext,
  anchorRange: Word.Range,
  sectionDisplayName?: string
) => {
  const fallback = {
    maxWidth: Math.max(DEFAULT_PAGE_WIDTH - DEFAULT_PAGE_MARGIN * 2, 72),
    maxHeight: Math.max(DEFAULT_PAGE_HEIGHT - DEFAULT_PAGE_MARGIN * 2, 72),
  };

  try {
    const sections = anchorRange.sections;
    sections.load("items");
    await context.sync();

    const targetSection = sections.items?.[0];

    if (!targetSection) {
      Logger.warn("wordInsertion.ts: No section found for image fit box, using fallback.", sectionDisplayName);
      return fallback;
    }

    const pageSetup = targetSection.pageSetup;
    pageSetup.load([
      "pageWidth",
      "pageHeight",
      "leftMargin",
      "rightMargin",
      "topMargin",
      "bottomMargin",
      "gutter",
      "gutterPosition",
      "orientation",
      "paperSize",
      "verticalAlignment",
    ]);
    await context.sync();

    const pageWidth = Number(pageSetup.pageWidth) || DEFAULT_PAGE_WIDTH;
    const pageHeight = Number(pageSetup.pageHeight) || DEFAULT_PAGE_HEIGHT;
    const leftMargin = Number(pageSetup.leftMargin) || 0;
    const rightMargin = Number(pageSetup.rightMargin) || 0;
    const topMargin = Number(pageSetup.topMargin) || 0;
    const bottomMargin = Number(pageSetup.bottomMargin) || 0;
    const gutter = Number(pageSetup.gutter) || 0;
    const gutterPosition = String(pageSetup.gutterPosition || "");

    const horizontalGutter = gutter > 0 && gutterPosition !== "Top" ? gutter : 0;
    const verticalGutter = gutter > 0 && gutterPosition === "Top" ? gutter : 0;

    const maxWidth = Math.max(pageWidth - leftMargin - rightMargin - horizontalGutter, 72);
    const maxHeight = Math.max(pageHeight - topMargin - bottomMargin - verticalGutter, 72);

    Logger.debug("wordInsertion.ts: Using dynamic section image fit box", {
      section: sectionDisplayName,
      pageWidth,
      pageHeight,
      leftMargin,
      rightMargin,
      topMargin,
      bottomMargin,
      gutter,
      gutterPosition,
      maxWidth,
      maxHeight,
      orientation: pageSetup.orientation,
      paperSize: pageSetup.paperSize,
      verticalAlignment: pageSetup.verticalAlignment,
    });

    return {
      maxWidth,
      maxHeight,
    };
  } catch (error) {
    Logger.warn("wordInsertion.ts: Failed to read section page setup, using fallback.", error, sectionDisplayName);
    return fallback;
  }
};

const fitInlinePictureWithinPage = async (
  context: Word.RequestContext,
  fitBox: { maxWidth: number; maxHeight: number },
  imageRange: Word.InlinePicture,
  item: any,
  preserveAspectRatio = true
) => {
  const maxWidth = Math.max(Number(fitBox?.maxWidth) || 0, 72);
  const maxHeight = Math.max(Number(fitBox?.maxHeight) || 0, 72);

  imageRange.load("width, height");
  await context.sync();

  const currentWidth = Number(imageRange.width) || Number(item?.width_px) || maxWidth;
  const currentHeight = Number(imageRange.height) || Number(item?.height_px) || maxHeight;

  if (preserveAspectRatio !== false && currentWidth > 0 && currentHeight > 0) {
    const widthScale = maxWidth / currentWidth;
    const heightScale = maxHeight / currentHeight;
    const appliedScale = Math.min(widthScale, heightScale);

    if (!Number.isFinite(appliedScale) || appliedScale <= 0) {
      return;
    }

    imageRange.lockAspectRatio = true;

    if (widthScale <= heightScale) {
      imageRange.width = Math.max(1, Math.round(currentWidth * appliedScale));
    } else {
      imageRange.height = Math.max(1, Math.round(currentHeight * appliedScale));
    }
  } else {
    imageRange.lockAspectRatio = false;
    imageRange.width = maxWidth;
    imageRange.height = maxHeight;
  }

  await context.sync();
};

const shouldCaptionImage = (item: any): boolean => {
  if (typeof item?.caption === "boolean") {
    return item.caption;
  }

  if (typeof item?.caption === "string") {
    return item.caption.trim().toLowerCase() === "true";
  }

  return false;
};

const getImageCaptionDescription = (item: any): string => {
  const captionText =
    item?.captionText ||
    item?.caption_text ||
    item?.captionDescription ||
    item?.caption_description ||
    item?.alt ||
    item?.altText ||
    item?.alt_text;

  return typeof captionText === "string" && captionText.trim() ? captionText.trim() : "TODO: Figure caption missing.";
};

const getImageStaticReference = (item: any): string | null => {
  const candidates = [
    item?.staticReference,
    item?.static_reference,
    item?.referenceText,
    item?.reference_text,
    item?.captionText,
    item?.caption_text,
    item?.captionDescription,
    item?.caption_description,
    item?.alt,
    item?.altText,
    item?.alt_text,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== "string") {
      continue;
    }

    const staticMatch = candidate.match(/Figure\s*-?\s*(?:\d+|\[[A-Z0-9]+\]|[A-Z0-9]+(?:[.#-][A-Z0-9]+)*)/i);

    if (staticMatch) {
      return staticMatch[0];
    }
  }

  return null;
};

const applyCaptionDraftStyle = async (
  context: Word.RequestContext,
  captionParagraph: Word.Paragraph,
  draftStyles: any
) => {
  captionParagraph.font.name = draftStyles?.fontFamily;
  captionParagraph.font.color = draftStyles?.fontColor;
  captionParagraph.font.size = draftStyles?.fontSize;
  captionParagraph.font.italic = draftStyles?.italic;
  captionParagraph.font.bold = true;
  await context.sync();
};

const insertImageItem = async (
  context: Word.RequestContext,
  fitBox: { maxWidth: number; maxHeight: number },
  insertionPoint: Word.Range,
  item: any,
  itemIndex: number,
  draftStyles: any
) => {
  const imageParagraph = insertionPoint.insertParagraph("", Word.InsertLocation.after);
  imageParagraph.styleBuiltIn = Word.BuiltInStyleName.normal;
  imageParagraph.alignment = Word.Alignment.centered;

  const imageRange = imageParagraph.insertInlinePictureFromBase64(item.content, Word.InsertLocation.end);
  imageRange.altTextDescription = item.alt || `Image ${item.page || ""}`.trim();

  await fitInlinePictureWithinPage(context, fitBox, imageRange, item, item.preserveAspectRatio !== false);

  const imageParagraphRange = imageParagraph.getRange();

  if (shouldCaptionImage(item)) {
    const bookmarkId = `Figure_${Date.now()}_${Math.random().toString(36).substring(2, 6)}_${itemIndex}`;
    const captionDescription = getImageCaptionDescription(item);
    const staticReference = getImageStaticReference(item);

    const captionParagraph = await createImmediateCaption(
      context,
      imageParagraphRange,
      bookmarkId,
      captionDescription,
      "Figure"
    );

    await applyCaptionDraftStyle(context, captionParagraph, draftStyles);

    const spacerParagraph = captionParagraph.insertParagraph("", Word.InsertLocation.after);
    prepareTextInsertionParagraph(spacerParagraph, draftStyles);
    await context.sync();

    if (item.pageBreakAfter) {
      spacerParagraph.insertBreak(Word.BreakType.page, Word.InsertLocation.after);
      await context.sync();

      const nextParagraph = spacerParagraph.insertParagraph("", Word.InsertLocation.after);
      prepareTextInsertionParagraph(nextParagraph, draftStyles);
      await context.sync();

      return {
        insertionPoint: nextParagraph.getRange(Word.RangeLocation.end),
        captionBookmark: { bookmarkId, captionParagraph, staticReference },
        excludeRanges: [imageParagraphRange, captionParagraph.getRange()],
      };
    }

    return {
      insertionPoint: spacerParagraph.getRange(Word.RangeLocation.end),
      captionBookmark: { bookmarkId, captionParagraph, staticReference },
      excludeRanges: [imageParagraphRange, captionParagraph.getRange()],
    };
  }

  const spacerParagraph = imageParagraph.insertParagraph("", Word.InsertLocation.after);
  prepareTextInsertionParagraph(spacerParagraph, draftStyles);
  await context.sync();

  if (item.pageBreakAfter) {
    spacerParagraph.insertBreak(Word.BreakType.page, Word.InsertLocation.after);
    await context.sync();

    const nextParagraph = spacerParagraph.insertParagraph("", Word.InsertLocation.after);
    prepareTextInsertionParagraph(nextParagraph, draftStyles);
    await context.sync();

    return {
      insertionPoint: nextParagraph.getRange(Word.RangeLocation.end),
      captionBookmark: null,
      excludeRanges: [],
    };
  }

  return {
    insertionPoint: spacerParagraph.getRange(Word.RangeLocation.end),
    captionBookmark: null,
    excludeRanges: [],
  };
};

// Inserts text and tables at a specific bookmark in the Word document
export const insertAtSection = async (
  targetBookmarkName: string,
  contentToInsert: any,
  chunkSize: number,
  setGeneratedSections,
  setSectionStatus,
  section,
  draftStyles,
  groundingMetadata?: GroundingInsertMetadata | null,
  renderOptions?: { replaceExisting?: boolean; strict?: boolean }
) => {
  return queuedWordRun(async (context) => {
    let isDocumentFormatted = false;

    // let sectionRange: Word.Range | null = null;
    if (Office.context.diagnostics.platform === Office.PlatformType.OfficeOnline) {
      const startBookmark = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_END");

      startBookmark.load("isNullObject, end");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        if (startBookmark.isNullObject) {
          Logger.warn("wordInsertion.ts: Start bookmark was not found.", section?.display_name);
        }
        if (endBookmark.isNullObject) {
          Logger.warn("wordInsertion.ts: End bookmark was not found.", section?.display_name);
        }
        Logger.warn("wordInsertion.ts: One or both bookmarks were not found.", section?.display_name);
        throw createMissingSectionBookmarkError(section?.display_name);
      }

      const combinedRange = startBookmark.expandTo(endBookmark);
      combinedRange.load("text");
      // sectionRange = combinedRange;
      await context.sync();

      const searchResults = combinedRange.search("Jenly.AI generated content will be inserted here.", {
        matchCase: false,
        matchWholeWord: true,
        ignorePunct: true,
      });

      searchResults.load("items");
      await context.sync();

      if (searchResults.items.length > 0) {
        Logger.debug("wordInsertion.ts: Found placeholder phrase after bookmark:", section?.display_name);
        searchResults.items[0].insertText(" ", Word.InsertLocation.replace);
        await context.sync();
      }

      combinedRange.clear();

      const bookmarkRange = startBookmark;
      const imageFitBox = await getSectionImageFitBox(context, bookmarkRange, section?.display_name);

      const paragraph = bookmarkRange.insertParagraph("", Word.InsertLocation.after);
      // Prevent inheriting heading formatting and keep generated body content justified.
      prepareTextInsertionParagraph(paragraph, draftStyles);
      await context.sync();

      let insertionPoint: Word.Range = paragraph.getRange(Word.RangeLocation.end);

      // Track tables for caption processing
      const tableInfos: TableInfo[] = [];
      const figureCaptionBookmarks: Array<{
        bookmarkId: string;
        captionParagraph: Word.Paragraph;
        staticReference?: string | null;
      }> = [];
      const excludeRangesFigure: Word.Range[] = [];

      try {
        for (let itemIndex = 0; itemIndex < contentToInsert.length; itemIndex++) {
          const item = contentToInsert[itemIndex];

          if (item.type === "text") {
            for (let i = 0; i < item.content.length; i += chunkSize) {
              const chunk = item.content.slice(i, i + chunkSize);

              const chunkRange = insertionPoint.insertText(chunk, Word.InsertLocation.end);

              await applyDraftStyle(chunkRange);

              await context.sync();

              insertionPoint = chunkRange;
            }
          } else if (item.type === "table") {
            // const tableRange = insertionPoint.expandTo(endBookmark);

            const wordTable = insertionPoint.insertTable(
              item.content.length,
              item.content[0].length,
              Word.InsertLocation.after,
              item.content
            );

            const tableRange = wordTable.getRange();

            await applyDraftStyle(tableRange);

            const wordRows = wordTable.rows;
            wordRows.load("items");

            await context.sync();

            for (var i = 0; i < wordRows.items.length; i++) {
              const curRow = wordRows.items[i];

              const cells = curRow.cells;
              cells.load("items");
              await context.sync();

              for (let cell of cells.items) {
                const cellBody = cell.body;
                const range = cellBody.getRange();
                range.styleBuiltIn = Word.BuiltInStyleName.normal;
              }

              if (i == 0) {
                const headerRow = curRow;
                headerRow.shadingColor = "lightGray";
                headerRow.preferredHeight = 35;
                headerRow.font.name = draftStyles.fontFamily;
                headerRow.font.color = draftStyles.fontColor;
                headerRow.font.size = draftStyles.fontSize + 1;
                headerRow.font.bold = true;
                headerRow.horizontalAlignment = "Centered";
                headerRow.verticalAlignment = "Center";
              } else {
                curRow.preferredHeight = 15;
                curRow.font.color = draftStyles.fontColor;
                curRow.font.name = draftStyles.fontFamily;
                curRow.font.size = draftStyles.fontSize;
                curRow.font.bold = false;
                curRow.verticalAlignment = "Center";
                curRow.horizontalAlignment = "Centered";
              }
            }

            await context.sync();

            await applyScriptMarkerFormattingToRange(context, tableRange);

            const newparagraph = wordTable.insertParagraph("", Word.InsertLocation.after);
            prepareTextInsertionParagraph(newparagraph, draftStyles);
            insertionPoint = newparagraph.getRange(Word.RangeLocation.end);

            // Generate unique bookmark ID for this table
            const generateUniqueBookmarkId = async (captionType: string): Promise<string> => {
              const timestamp = Date.now().toString(36);
              const randomPart = Math.random().toString(36).substring(2, 6);
              return `${captionType}_${timestamp}_${randomPart}_${itemIndex}`;
            };

            const bookmarkId = await generateUniqueBookmarkId("Table");

            // Generate caption description from table data or use default
            const captionDescription =
              item.caption ||
              item.description ||
              (item.content[0] ? item.content[0].join(" ").substring(0, 50) : `Table ${itemIndex + 1} Data`);

            // Store table info for later caption processing
            tableInfos.push({
              tableRange,
              bookmarkId,
              captionDescription,
            });
          } else if (item.type === "image") {
            try {
              const imageInsertion = await insertImageItem(
                context,
                imageFitBox,
                insertionPoint,
                item,
                itemIndex,
                draftStyles
              );
              insertionPoint = imageInsertion.insertionPoint;

              if (imageInsertion.captionBookmark) {
                figureCaptionBookmarks.push(imageInsertion.captionBookmark);
                excludeRangesFigure.push(...imageInsertion.excludeRanges);
              }

              Logger.debug("wordInsertion.ts: Image inserted successfully", section?.display_name);
            } catch (error) {
              Logger.warn("wordInsertion.ts: Failed to insert image", error, section?.display_name);
              if (renderOptions?.strict) throw error;
            }
          }
        }

        // Recalculate section range to include all inserted content
        const startBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_START");
        const endBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_END");

        startBookmarkRange.load("isNullObject");
        endBookmarkRange.load("isNullObject");
        await context.sync();

        if (!startBookmarkRange.isNullObject && !endBookmarkRange.isNullObject) {
          // Expand the range to include all content between bookmarks
          const updatedSectionRange = startBookmarkRange.expandTo(endBookmarkRange);

          // Create captions and cross-references for all tables in this section
          if (tableInfos.length > 0) {
            await createCaptionsForSection(context, updatedSectionRange, tableInfos, "Table");
          }

          if (figureCaptionBookmarks.length > 0) {
            await createCrossReferences(
              context,
              updatedSectionRange,
              figureCaptionBookmarks,
              excludeRangesFigure,
              "Figure"
            );
          }
        }
      } catch (error) {
        Logger.error("wordInsertion.ts: Error during content insertion for section:", error, section?.display_name);
        if (renderOptions?.strict) throw error;
      }
    } else {
      const startBookmark = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_START");
      const endBookmark = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_END");

      startBookmark.load("isNullObject, end");
      endBookmark.load("isNullObject");
      await context.sync();

      if (startBookmark.isNullObject || endBookmark.isNullObject) {
        if (startBookmark.isNullObject) {
          Logger.warn("wordInsertion.ts: Start bookmark was not found.", section?.display_name);
        }
        if (endBookmark.isNullObject) {
          Logger.warn("wordInsertion.ts: End bookmark was not found.", section?.display_name);
        }
        Logger.warn("wordInsertion.ts: One or both bookmarks were not found.", section?.display_name);
        throw createMissingSectionBookmarkError(section?.display_name);
      }

      const combinedRange = startBookmark.expandTo(endBookmark);
      combinedRange.load("text");
      await context.sync();

      const searchResults = combinedRange.search("Jenly.AI generated content will be inserted here.", {
        matchCase: false,
        matchWholeWord: true,
        ignorePunct: true,
      });

      searchResults.load("items");
      await context.sync();

      if (searchResults.items.length > 0) {
        Logger.debug("wordInsertion.ts: Found placeholder phrase after bookmark:", section?.display_name);
        searchResults.items[0].insertText(" ", Word.InsertLocation.replace);
        await context.sync();
      }

      const bookmarkRange = startBookmark;
      if (renderOptions?.replaceExisting) {
        // Desktop insertion normally appends after the start bookmark because
        // generation callers already clear their old content. Replay owns that
        // replacement explicitly, retaining the surrounding template.
        combinedRange.clear();
        await context.sync();
      }
      const imageFitBox = await getSectionImageFitBox(context, bookmarkRange, section?.display_name);

      const paragraph = bookmarkRange.insertParagraph("", Word.InsertLocation.after);
      prepareTextInsertionParagraph(paragraph, draftStyles);
      await context.sync();

      let insertionPoint: Word.Range = paragraph.getRange(Word.RangeLocation.end);

      insertionPoint.insertText("", Word.InsertLocation.replace);

      // Track tables for caption processing
      const tableCaptionBookmarks: Array<{
        bookmarkId: string;
        captionParagraph: Word.Paragraph;
        staticReference?: string | null;
        refId?: string | null;
      }> = [];
      const figureCaptionBookmarks: Array<{
        bookmarkId: string;
        captionParagraph: Word.Paragraph;
        staticReference?: string | null;
      }> = [];

      // Track all exclude ranges for cross-reference filtering
      const excludeRangesFigure: Word.Range[] = [];
      const excludeRangesTable: Word.Range[] = [];
      const pendingTableInstructions: TableInstruction[] = [];
      let lastInsertedBlockType: "text" | "table" | "image" | null = null;

      for (let itemIndex = 0; itemIndex < contentToInsert.length; itemIndex++) {
        const item = contentToInsert[itemIndex];
        Logger.debug(`wordInsertion.ts: Content to insert`, item, itemIndex, section?.display_name);

        if (item.type === "text") {
          const nextItem = contentToInsert[itemIndex + 1];
          const tableInstructionExtraction = extractTableInstructionsFromText(item.content);
          pendingTableInstructions.push(...tableInstructionExtraction.instructions);
          item.content = tableInstructionExtraction.content;

          if (!item.content) {
            continue;
          }

          const lines = item.content.split("\n");
          const linesToInsert = [];

          const isNextItemTable = nextItem && nextItem.type === "table";

          for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex];
            const isLastLine = lineIndex === lines.length - 1;
            const isTableCaption = parseTableCaptionLine(line);

            if (isLastLine && isTableCaption && isNextItemTable) {
              Logger.debug(
                "wordInsertion.ts: Skipping text line, will be used as table caption:",
                line,
                section?.display_name
              );
              continue;
            } else {
              linesToInsert.push(line);
            }
          }

          if (linesToInsert.length > 0) {
            const textToInsert = linesToInsert.join("\n");
            for (let i = 0; i < textToInsert.length; i += chunkSize) {
              const chunk = textToInsert.slice(i, i + chunkSize);
              const chunkRange = insertionPoint.insertText(chunk, Word.InsertLocation.end);
              await applyDraftStyle(chunkRange);
              await context.sync();
              insertionPoint = chunkRange;
            }

            lastInsertedBlockType = "text";
          }
        } else if (item.type === "table") {
          const tableInstruction = pendingTableInstructions.shift() || null;

          if (lastInsertedBlockType === "table") {
            const spacerParagraph = insertionPoint.insertParagraph("", Word.InsertLocation.after);
            prepareTextInsertionParagraph(spacerParagraph, draftStyles);
            await context.sync();
            insertionPoint = spacerParagraph.getRange(Word.RangeLocation.end);
          }

          const prepParagraph = insertionPoint.insertParagraph("", Word.InsertLocation.after);
          await context.sync();

          let captionDescription = "";
          const prevItem = contentToInsert[itemIndex - 1];
          let staticReferenceText: string | null = null;
          let staticCaptionLabel: string | null = null;
          const instructionCaption = tableInstruction?.captionText
            ? resolveTableCaptionSource(tableInstruction.captionText, true)
            : null;

          if (instructionCaption) {
            captionDescription = instructionCaption.captionDescription;
            staticReferenceText = instructionCaption.staticReferenceText;
            staticCaptionLabel = instructionCaption.staticCaptionLabel;
            Logger.debug(
              "wordInsertion.ts: Using JENLY_TABLE token caption:",
              captionDescription,
              section?.display_name
            );

            if (staticReferenceText) {
              Logger.debug(
                `wordInsertion.ts: Captured static reference from JENLY_TABLE token: "${staticReferenceText}"`,
                section?.display_name
              );
            }
          } else if (itemIndex > 0 && prevItem && prevItem.type === "text") {
            const prevContentLines = prevItem.content.trim().split("\n");
            const lastLine = prevContentLines[prevContentLines.length - 1].trim();

            const tableMatch = resolveTableCaptionSource(lastLine, false);

            if (tableMatch) {
              captionDescription = tableMatch.captionDescription;
              Logger.debug(
                "wordInsertion.ts: Using last line of prev text as caption:",
                captionDescription,
                section?.display_name
              );

              staticReferenceText = tableMatch.staticReferenceText;
              staticCaptionLabel = tableMatch.staticCaptionLabel;
              Logger.debug(
                `wordInsertion.ts: Captured static reference for search: "${staticReferenceText}"`,
                section?.display_name
              );
            }
          }

          if (!captionDescription) {
            captionDescription = "TODO: Table caption missing.";
          }

          const hasCustomIdAndSeq = !!(tableInstruction?.id && tableInstruction?.seqType);

          const bookmarkId = `Table_${Date.now()}_${Math.random().toString(36).substring(2, 6)}_${itemIndex}`;

          Logger.debug("wordInsertion.ts: Target bookmark name", bookmarkId, section?.display_name);

          let captionParagraph: Word.Paragraph | null = null;
          const isAppendixSection = !!section?.display_name?.startsWith("APPENDIX");
          const shouldShowCaption = !isAppendixSection && shouldCreateTableCaption(tableInstruction);
          const captionPosition = tableInstruction?.captionPosition || "above";

          if (shouldShowCaption && captionPosition === "above") {
            const captionRange = prepParagraph.getRange();

            captionParagraph = await createImmediateCaption(
              context,
              captionRange,
              bookmarkId,
              captionDescription,
              "Table",
              staticCaptionLabel,
              Word.InsertLocation.before,
              hasCustomIdAndSeq ? tableInstruction?.id : undefined,
              hasCustomIdAndSeq ? tableInstruction?.seqType : undefined
            );

            captionParagraph.font.name = draftStyles.fontFamily;
            captionParagraph.font.color = draftStyles.fontColor;
            captionParagraph.font.size = draftStyles.fontSize;
            captionParagraph.font.italic = true;
            applyCaptionFontStyle(
              captionParagraph,
              tableInstruction?.captionFontStyle ?? DEFAULT_TABLE_INSTRUCTION.captionFontStyle
            );
            await context.sync();
          }

          // Moving the insertion point in a new line
          const tableParagraph = captionParagraph
            ? captionParagraph.insertParagraph("", Word.InsertLocation.after)
            : prepParagraph;
          await context.sync();
          insertionPoint = tableParagraph.getRange(Word.RangeLocation.end);

          Logger.debug("wordInsertion.ts: Checking all table values", item, section?.display_name);

          const colCount = item.content[0].length;
          const sanitizedContent = item.content.map((row: string[]) =>
            row.map((cell) =>
              (cell || "")
                .replace(/<!--[\s\S]*?-->/g, "")
                .replace(/&nbsp;/g, " ")
            )
          );
          const normalizedContent = sanitizedContent.map((row: string[]) => {
            if (row.length === colCount) return row;
            if (row.length > colCount) {
              const trimmed = row.slice(0, colCount);
              trimmed[colCount - 1] = row.slice(colCount - 1).join("\n");
              Logger.warn(
                `wordInsertion.ts: Table row had ${row.length} cells, expected ${colCount}. Collapsed overflow into last column.`,
                section?.display_name
              );
              return trimmed;
            }
            return [...row, ...Array(colCount - row.length).fill("")];
          });
          const removedSourceHeaderRowCount =
            tableInstruction?.showHeaderRow === false
              ? Math.min(tableInstruction.headerRowCount, normalizedContent.length)
              : 0;
          const tableContentToInsert = normalizedContent.slice(removedSourceHeaderRowCount);

          if (tableContentToInsert.length === 0) {
            continue;
          }

          const wordTable = insertionPoint.insertTable(
            tableContentToInsert.length,
            colCount,
            Word.InsertLocation.after,
            tableContentToInsert
          );

          const headerRowsToStyle = getHeaderRowsToStyle(tableInstruction, tableContentToInsert.length);
          wordTable.headerRowCount = getRepeatingHeaderRowCount(tableInstruction, headerRowsToStyle);

          await context.sync();

          const tableRange = wordTable.getRange();
          // Apply default font to the whole table range to ensure consistent styling
          await applyDraftStyle(tableRange);
          // tableRange.font.name = "Courier New";
          // tableRange.font.color = "#2E8B57";

          Logger.debug("wordInsertion.ts: Starting table buildup", section?.display_name);

          const wordRows = wordTable.rows;
          wordRows.load("items");

          const alignments = item.alignments || [];

          const alignmentMap = {
            left: "Left",
            center: "Centered",
            right: "Right",
            default: "Left",
          };

          await context.sync();

          for (let j = 0; j < wordRows.items.length; j++) {
            const curRow = wordRows.items[j];

            const cells = curRow.cells;
            cells.load("items");
            await context.sync();

            for (let k = 0; k < cells.items.length; k++) {
              const cell = cells.items[k];
              const cellBody = cell.body;
              const range = cellBody.getRange();
              range.styleBuiltIn = Word.BuiltInStyleName.normal;
              if (cellBody.paragraphs) {
                cellBody.paragraphs.load("items");
              }
            }
            await context.sync();

            for (let k = 0; k < cells.items.length; k++) {
              const cell = cells.items[k];
              const mdAlignment = alignments[k] || "default";
              const wordAlignment = alignmentMap[mdAlignment] || "Left";
              if (cell.body?.paragraphs?.items) {
                for (const p of cell.body.paragraphs.items) {
                  p.alignment = wordAlignment as Word.Alignment;
                }
              }
            }

            if (j < headerRowsToStyle) {
              const headerRow = curRow;
              headerRow.shadingColor = "lightGray";
              headerRow.preferredHeight = 35;
              headerRow.font.name = draftStyles.fontFamily;
              headerRow.font.color = draftStyles.fontColor;
              headerRow.font.size = draftStyles.fontSize + 1;
              headerRow.font.bold = true;
              headerRow.verticalAlignment = "Center";
              // Ensure character-level bold in each header cell so it isn't overridden by cell/table formatting
              const headerCells = headerRow.cells;
              headerCells.load("items");
              await context.sync();
              for (let hCell of headerCells.items) {
                const hRange = hCell.body.getRange();
                hRange.font.bold = true;
              }
            } else {
              curRow.preferredHeight = 15;
              curRow.font.color = draftStyles.fontColor;
              curRow.font.name = draftStyles.fontFamily;
              curRow.font.size = draftStyles.fontSize;
              curRow.font.bold = false;
              curRow.verticalAlignment = "Center";
            }
          }

          await context.sync();

          Logger.debug("wordInsertion.ts: Applying table merges:", item.merges, section?.display_name);

          if (item.merges && item.merges.length > 0) {
            Logger.debug("wordInsertion.ts: Applying table merges:", item.merges, section?.display_name);

            const sortedMerges = [...item.merges].sort((a, b) => {
              if (b.row !== a.row) return b.row - a.row;
              return b.col - a.col;
            });

            // We iterate through the merge definitions calculated by the parser
            for (const merge of sortedMerges) {
              try {
                const originalTopRow = merge.row;
                const firstCell = merge.col;
                const rowSpan = merge.rowSpan || 1;
                const colSpan = merge.colSpan || 1;

                // Calculate the inclusive boundaries for mergeCells
                const originalBottomRow = originalTopRow + rowSpan - 1;
                const topRow = originalTopRow - removedSourceHeaderRowCount;
                const bottomRow = originalBottomRow - removedSourceHeaderRowCount;
                const lastCell = firstCell + colSpan - 1;

                // Safety check to ensure we don't crash Word if LLM gave bad coordinates
                if (
                  originalTopRow >= removedSourceHeaderRowCount &&
                  topRow >= 0 &&
                  firstCell >= 0 &&
                  // Ensure we don't try to merge outside the table bounds
                  bottomRow < tableContentToInsert.length &&
                  lastCell < colCount &&
                  // Ensure we aren't trying to merge a cell into itself (Word sometimes dislikes 1x1 merges)
                  (rowSpan > 1 || colSpan > 1)
                ) {
                  Logger.debug(
                    "wordInsertion.ts: Attempting Merge: Top=${topRow}, Left=${firstCell}, Bottom=${bottomRow}, Right=${lastCell}",
                    section?.display_name
                  );

                  wordTable.mergeCells(topRow, firstCell, bottomRow, lastCell);
                  await context.sync();
                }
              } catch (mergeError) {
                Logger.warn(
                  "wordInsertion.ts: Failed to merge specific cell:",
                  merge,
                  mergeError,
                  section?.display_name
                );

                await context.sync().catch(() => {});
              }
            }

            // Sync again to commit the merges
            await context.sync();
          }

          await applyScriptMarkerFormattingToRange(context, tableRange);

          const newparagraph = wordTable.insertParagraph("", Word.InsertLocation.after);
          prepareTextInsertionParagraph(newparagraph, draftStyles);
          const footnoteTailParagraph = await insertTableFootnotes(context, newparagraph, tableInstruction?.footnotes);

          if (shouldShowCaption && captionPosition === "below") {
            captionParagraph = await createImmediateCaption(
              context,
              footnoteTailParagraph.getRange(),
              bookmarkId,
              captionDescription,
              "Table",
              staticCaptionLabel,
              Word.InsertLocation.before,
              hasCustomIdAndSeq ? tableInstruction?.id : undefined,
              hasCustomIdAndSeq ? tableInstruction?.seqType : undefined
            );

            captionParagraph.font.name = draftStyles.fontFamily;
            captionParagraph.font.color = draftStyles.fontColor;
            captionParagraph.font.size = draftStyles.fontSize;
            captionParagraph.font.italic = true;
            applyCaptionFontStyle(
              captionParagraph,
              tableInstruction?.captionFontStyle ?? DEFAULT_TABLE_INSTRUCTION.captionFontStyle
            );
            await context.sync();

            const paragraphAfterCaption = captionParagraph.insertParagraph("", Word.InsertLocation.after);
            prepareTextInsertionParagraph(paragraphAfterCaption, draftStyles);
            await context.sync();
            insertionPoint = paragraphAfterCaption.getRange(Word.RangeLocation.end);
          } else {
            insertionPoint = footnoteTailParagraph.getRange(Word.RangeLocation.end);
          }

          await context.sync();

          if (captionParagraph) {
            tableCaptionBookmarks.push({
              bookmarkId,
              captionParagraph,
              staticReference: staticReferenceText,
              refId: hasCustomIdAndSeq ? tableInstruction?.id : undefined,
            });

            // Exclude only the caption itself so table-cell references remain
            // eligible for cross-referencing, including self-references inside
            // long multi-page tables.
            excludeRangesTable.push(captionParagraph.getRange());
          }

          lastInsertedBlockType = "table";
        } else if (item.type === "image") {
          try {
            Logger.debug("wordInsertion.ts: Starting image insertion", item, section?.display_name);
            const imageInsertion = await insertImageItem(
              context,
              imageFitBox,
              insertionPoint,
              item,
              itemIndex,
              draftStyles
            );
            insertionPoint = imageInsertion.insertionPoint;

            if (imageInsertion.captionBookmark) {
              figureCaptionBookmarks.push(imageInsertion.captionBookmark);
              excludeRangesFigure.push(...imageInsertion.excludeRanges);
            }

            Logger.debug("wordInsertion.ts: Image inserted successfully", section?.display_name);
            lastInsertedBlockType = "image";
          } catch (error) {
            Logger.warn("wordInsertion.ts: Failed to insert image", error, section?.display_name);
            if (renderOptions?.strict) throw error;
          }
        }
      }

      // Recalculate section range to include all inserted content
      const startBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_START");
      const endBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_END");

      startBookmarkRange.load("isNullObject");
      endBookmarkRange.load("isNullObject");
      await context.sync();

      if (!startBookmarkRange.isNullObject && !endBookmarkRange.isNullObject) {
        await formatSection(targetBookmarkName, context);
        await context.sync();

        isDocumentFormatted = true;

        // Expand the range to include all content between bookmarks
        const updatedSectionRange = startBookmarkRange.expandTo(endBookmarkRange);

        // Create captions and cross-references for all tables in this section
        if (tableCaptionBookmarks.length > 0) {
          await createCrossReferences(context, updatedSectionRange, tableCaptionBookmarks, excludeRangesTable, "Table");
        }

        if (figureCaptionBookmarks.length > 0) {
          await createCrossReferences(
            context,
            updatedSectionRange,
            figureCaptionBookmarks,
            excludeRangesFigure,
            "Figure"
          );
        }
      }
    }

    if (isDocumentFormatted === false) {
      await formatSection(targetBookmarkName, context);
      await context.sync();
    }

    if (groundingMetadata?.taskId) {
      const startBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_START");
      const endBookmarkRange = context.document.getBookmarkRangeOrNullObject(targetBookmarkName + "_END");

      startBookmarkRange.load("isNullObject");
      endBookmarkRange.load("isNullObject");
      await context.sync();

      if (!startBookmarkRange.isNullObject && !endBookmarkRange.isNullObject) {
        const updatedSectionRange = startBookmarkRange.expandTo(endBookmarkRange);
        Logger.debug("wordInsertion.ts: Applying grounding marker content controls for section.", {
          sectionId: section?.id || null,
          sectionDisplayName: section?.display_name || null,
          targetBookmarkName,
          taskId: groundingMetadata.taskId,
        });
        try {
          await applyGroundingMarkerContentControls(context, updatedSectionRange, groundingMetadata);
        } catch (groundingError) {
          Logger.error("wordInsertion.ts: Failed to apply grounding marker content controls:", groundingError);
        }
      }
    }

    if (typeof setGeneratedSections === "function") {
      setGeneratedSections((prev) => [...prev, section]);
    }
    if (typeof setSectionStatus === "function") {
      setSectionStatus((prev) => ({ ...prev, [section.id]: true }));
    }
  });
};

export const insertTemplateDocument = async (predefinedDocumentTemplateBase64: string) => {
  await Word.run(async (context) => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    context.document.insertFileFromBase64(predefinedDocumentTemplateBase64, Word.InsertLocation.start, {
      importTheme: true,
      importStyles: true,
      importParagraphSpacing: true,
      importPageColor: true,
      importChangeTrackingMode: true,
      importCustomProperties: true,
      importCustomXmlParts: true,
      importDifferentOddEvenPages: true,
    });
    await context.sync();

    try {
      Logger.debug("wordInsertion.ts: Appendix logs: Template inserted. Starting Appendix search...");

      const appendixSearches = [];

      const searchPatterns = ["APPENDIX *^13", "Appendix *^13", "appendix *^13"];
      const appendixSearchOptions = { matchWildcards: true, matchCase: true };

      for (const searchPattern of searchPatterns) {
        const bodyResults = context.document.body.search(searchPattern, appendixSearchOptions);
        bodyResults.load("items/text");
        appendixSearches.push(bodyResults);
      }

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      for (const section of sections.items) {
        const headers = [section.getHeader("Primary"), section.getHeader("FirstPage"), section.getHeader("EvenPages")];
        const footers = [section.getFooter("Primary"), section.getFooter("FirstPage"), section.getFooter("EvenPages")];

        for (const header of headers) {
          for (const searchPattern of searchPatterns) {
            const hResults = header.search(searchPattern, appendixSearchOptions);
            hResults.load("items/text");
            appendixSearches.push(hResults);
          }
        }

        for (const footer of footers) {
          for (const searchPattern of searchPatterns) {
            const fResults = footer.search(searchPattern, appendixSearchOptions);
            fResults.load("items/text");
            appendixSearches.push(fResults);
          }
        }
      }

      await context.sync();

      let isBookmarkCreated = false;

      for (const results of appendixSearches) {
        if (results.items && results.items.length > 0) {
          for (const item of results.items) {
            if (item.text.indexOf("\t") !== -1) {
              Logger.debug("wordInsertion.ts: Appendix logs: Skipped potential table of contents entry:", item.text);
              continue;
            }

            const rawText = item.text.trim();

            const match = rawText.match(/^APPENDIX\s+([0-9.]+)/i);

            if (match) {
              const textForId = match[0];

              Logger.debug("wordInsertion.ts: Appendix logs: Found valid Appendix:", textForId);

              let normalizedText = textForId;
              if (normalizedText.endsWith(".")) {
                normalizedText = normalizedText.slice(0, -1);
              }

              const cleanText = normalizedText
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "_")
                .replace(/_+/g, "_");

              const bookmarkId = "_" + cleanText;

              Logger.debug(`wordInsertion.ts: Appendix logs: Template Bookmark Created: ${bookmarkId}`);

              const safeBookmarkId = bookmarkId.substring(0, 35);

              const refinedRange = item.search(normalizedText, { matchWildcards: false }).getFirst();

              try {
                refinedRange.insertBookmark(safeBookmarkId);

                Logger.debug(
                  `wordInsertion.ts: Appendix logs: Template Bookmark Created: [${normalizedText}] => ${safeBookmarkId}`
                );

                isBookmarkCreated = true;
              } catch (error) {
                Logger.error(`wordInsertion.ts: Appendix logs: Could not create bookmark`, error);
              }
            } else {
              Logger.debug("wordInsertion.ts: Appendix logs: Ignored false positive line:", rawText);
            }
          }
        }
      }

      if (isBookmarkCreated) {
        await context.sync();
      }
    } catch (error) {
      Logger.error(
        "wordInsertion.ts: Appendix logs: Error while searching for APPENDIX and while creating bookmarks",
        error
      );
    }
  });
};
